import {
  Box,
  Button,
  ButtonSize,
  ButtonVariant,
  Space,
} from "@gocardless/flux-react";
import { Trans } from "@lingui/macro";
import Cookies from "js-cookie";

import { useSetupBillingDetails } from "../billing-details/useSetupBillingDetails";
import PageTitle from "../common/components/PageTitle";
import { MerchantOnboardingSetupEvents } from "../common/constants/MerchantOnboardingSetupEvents";
import { useSegmentForSetup } from "../common/hooks/useSegmentForSetup";
import useToast from "../common/hooks/useToast";

import BankSelectFailureNotification from "./features/bank-select-failure/BankSelectFailureNotification";
import AddBankForm, { AddBankFormVariant } from "./forms/AddBankForm";
import { useBankDetails } from "./hooks/useBankDetails";
import { AdditionalCurrenciesSection } from "./sections/AdditionalCurrenciesSection";
import { BankDetailsCardSection } from "./sections/BankDetailsCardSection";
import { useBankDetailsConfig } from "./hooks/useBankDetailsConfig";

import { criteoProperties } from "src/technical-integrations/third-party-tracking/criteo/Criteo";
import { CookieStorage } from "src/common/constants/storage";
import {
  REDDIT_TRACKING_EVENT,
  trackReddit,
} from "src/technical-integrations/third-party-tracking/Reddit";
import { usePrimaryCreditor } from "src/libraries/creditor/hooks";
import { Route } from "src/common/routing";
import { Currency } from "src/common/currencies";
import { CountryCodes } from "src/common/country";

const BankDetails = (): JSX.Element => {
  const { sendEvent } = useSegmentForSetup();
  const impactClickID = Cookies.get(CookieStorage.ImpactClickID);
  const awinClickID = Cookies.get(CookieStorage.AWinClickID);
  const {
    primaryAccount,
    additionalAccounts,
    refreshSetup,
    primaryCurrency,
    additionalCurrencies: moreCurrencies,
    schemeIdentifiers,
    bankAuthorisation,
    completed,
  } = useBankDetails();

  const { skip } = useSetupBillingDetails();
  const { onSuccess } = useToast(
    skip ? Route.AccountStatus : Route.BillingDetails
  );

  const creditor = usePrimaryCreditor();

  const copVariantEnabled = creditor?.cop_enabled;

  const {
    schemeIdentifierCurrencies,
    internationalPaymentsEnabled,
    showPrimaryBankForm,
    setShowPrimaryBankForm,
    showAddAdditionalBanks,
    setShowAddAdditionalBanks,
    pageTitle,
  } = useBankDetailsConfig({
    schemeIdentifiers,
    primaryAccount,
    bankAuthorisation,
    creditor,
    additionalAccounts,
    copVariantEnabled: copVariantEnabled,
  });

  return (
    <Box className="fs-unmask">
      <PageTitle title={pageTitle} />
      {!showPrimaryBankForm && primaryAccount ? (
        <BankDetailsCardSection
          primaryAccount={primaryAccount}
          bankAuthorisation={bankAuthorisation}
          internationalPaymentsEnabled={internationalPaymentsEnabled}
          primaryCurrency={primaryCurrency}
          setShowAddAdditionalBanks={setShowAddAdditionalBanks}
          setShowPrimaryBankForm={setShowPrimaryBankForm}
        />
      ) : (
        <>
          {window.location.search.includes("bankSelectFailed=true") && (
            <BankSelectFailureNotification />
          )}
          {(internationalPaymentsEnabled || showPrimaryBankForm) && (
            <>
              <AddBankForm
                variant={AddBankFormVariant.Setup}
                details={primaryAccount}
                onSubmit={() => {
                  sendEvent(
                    MerchantOnboardingSetupEvents.BankAccountAccountAdded,
                    {
                      page_version: "variant",
                    }
                  );
                  window.scrollTo(0, 0);
                  refreshSetup();
                  setShowPrimaryBankForm(false);
                }}
                defaultCountry={creditor?.geo as CountryCodes}
                currency={primaryCurrency as Currency}
                isPrimaryAccount={true}
                copVariantEnabled={copVariantEnabled}
              />
              <Space v={3} />
            </>
          )}
        </>
      )}
      {(showAddAdditionalBanks ||
        (!internationalPaymentsEnabled &&
          (moreCurrencies.length > 0 || !primaryAccount))) && (
        <AdditionalCurrenciesSection
          showPrimaryBankForm={showPrimaryBankForm}
          primaryAccount={primaryAccount}
          showAddAdditionalBanks={showAddAdditionalBanks}
          additionalAccounts={additionalAccounts}
          schemeIdentifierCurrencies={schemeIdentifierCurrencies}
          refreshSetup={refreshSetup}
        />
      )}
      <Box spaceAbove={2}>
        <Button
          size={ButtonSize.Lg}
          variant={ButtonVariant.PrimaryOnLight}
          disabled={!completed}
          onClick={() => {
            sendEvent(MerchantOnboardingSetupEvents.BankAccountCompleted, {
              page_version: "variant",
              category: "Begin checkout",
              revenue: 50,
              context: {
                referrer: {
                  type: "impactRadius",
                  id: impactClickID,
                },
              },
              awinclickid: awinClickID,
              ...criteoProperties,
            });
            trackReddit().trackRedditEvent(
              REDDIT_TRACKING_EVENT.BANK_ACCOUNT_ADDED
            );
            refreshSetup().then(() => onSuccess({ notify: true }));
          }}
        >
          <Trans id="setup.bank-details.continue">Continue</Trans>
        </Button>
      </Box>
    </Box>
  );
};

export default BankDetails;
